import { type } from 'arktype';

import type { TranslatableText } from '@zag/core/common';

/**
 * Build an Arktype schema describing a `TranslatableText<Fields>` payload for
 * a specific entity. The shape is intentionally open: every language slot is
 * optional, every field within a slot is optional, and `null` is accepted
 * everywhere strings are accepted (for explicit "clear this field" updates).
 *
 * Use this factory inside `create*` and `update*` schemas:
 *
 * @example
 * ```ts
 * export const createLocationSchema = type({
 *   // ... existing fields
 *   'translations?': translatableTextSchema(['name', 'description']),
 * });
 * ```
 */
export function translatableTextSchema<const Fields extends readonly string[]>(fields: Fields): ReturnType<typeof type<object>> {
  const fieldShape: Record<string, string> = {};
  for (const field of fields) {
    fieldShape[`${field}?`] = 'string | null';
  }
  const slot = type(fieldShape).configure({
    description: `Per-language slot containing optional ${fields
      .map(f => `\`${f}\``)
      .join(', ')} value(s). \`null\` clears the field for that language.`,
  });

  const exampleEn = Object.fromEntries(fields.map((f, i) => [f, i === 0 ? 'Example value' : null]));
  const exampleDe: Record<string, string | null> = fields.length > 0 ? { [fields[0] as string]: 'Beispielwert' } : {};

  return type({
    'en?': slot,
    'de?': slot,
    'fr?': slot,
    'it?': slot,
  }).configure({
    description:
      'Per-language map of translatable fields. Languages omitted from the map are left untouched on update. ' +
      'Fields omitted from a language slot are left untouched. Setting a field to `null` clears it for that language.',
    examples: [{ en: exampleEn, de: exampleDe }],
  });
}

/**
 * Validate an unknown payload against the schema produced by
 * {@link translatableTextSchema} and narrow it to a typed
 * `TranslatableText<Fields>`.
 */
export function parseTranslatableText<Fields extends string>(
  payload: unknown,
  fields: readonly Fields[]
): TranslatableText<Fields> | undefined {
  if (payload == null) return undefined;
  const schema = translatableTextSchema(fields);
  const validated = schema(payload);
  if (validated instanceof type.errors) {
    throw new Error(`Invalid translations payload: ${validated.summary}`);
  }
  return validated as TranslatableText<Fields>;
}
